/**
 * Node filters shared by the GLB export call sites.
 *
 * The editor parks invisible picker meshes inside object subtrees (Text and
 * ParticleSystem selection proxies, Box/Sphere collider pickers). The render
 * path filters them by suffix (TraceScene, ContactShadow, SelectionOutline);
 * exporters must apply the same exclusion or a phantom slab serializes into
 * the GLB whenever a helper node falls inside the exported subtree.
 */
import type { Node } from "@babylonjs/core/node";

import type { SceneObject } from "../core/SceneObject";
import { Logger } from "../utils/Logger";
import { COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from "../behaviors/boxcollider";
import { TEXT_BODY_SUFFIX } from "../behaviors/text/textBody";

/**
 * Meshes that must never serialize: editor helpers (Text/collider pickers),
 * and an extruded Text's live body, which the export bakes from the Text.
 */
export function isEditorHelperMesh(node: Node): boolean {
    return (
        node.name.endsWith(COLLIDER_MESH_SUFFIX) ||
        node.name.endsWith(SELECTION_MESH_SUFFIX) ||
        node.name.endsWith(TEXT_BODY_SUFFIX)
    );
}

/**
 * The exclusion is a pure SUFFIX match with no provenance, so a user-imported
 * mesh that happens to be named `door__collider` renders in the editor and
 * then silently vanishes from every export. Until the five creation sites
 * stamp a real flag, say so rather than dropping it in silence.
 *
 * At DEBUG, not warn: every Text, ParticleSystem and Collider parks a
 * suffix-matched helper by design, so a 30-Text scene would emit 30 warnings
 * per save and drown the ones that mean something went wrong. Warn is for
 * failures; this is per-object bookkeeping.
 */
export function warnHelperMeshExcluded(where: string, nodes: readonly Node[]): void {
    if (nodes.length === 0) return;
    Logger.debug(
        `[${where}] excluded ${nodes.length} editor helper mesh(es) from the export by name suffix: `
        + `${nodes.map(n => n.name).join(', ')}. A user mesh named this way is dropped too.`,
    );
}

/**
 * The Babylon nodes a SceneObject owns DIRECTLY: every descendant of its
 * transform, stopping at nested SceneObjects and skipping editor helpers.
 *
 * A nested SceneObject is its OWN export record with its own `src` and its
 * own bake. Walking straight through one puts the same geometry in the
 * archive twice — baked into the parent's asset and loaded again from the
 * child's record — and makes the parent's bake plan claim meshes another
 * plan owns. Both bake sites take their mesh sets from here.
 *
 * `excluded` collects the helper meshes dropped along the way, for callers
 * that report them (see {@link warnHelperMeshExcluded}).
 */
export function ownedExportNodes(object: SceneObject, excluded?: Node[]): Node[] {
    // Detached objects and partial test scenes have no registry to resolve
    // ownership through — no registry means no boundaries, which is exactly
    // the pre-existing "everything below the transform" behaviour.
    const scene = object.traceScene as
        { findSceneObjectByTransform?(node: Node): SceneObject | undefined } | undefined;
    const resolve = typeof scene?.findSceneObjectByTransform === 'function'
        ? scene.findSceneObjectByTransform.bind(scene)
        : null;

    const owned: Node[] = [];
    // The object's ROOT is never visited — the walk starts at its children.
    // Safe only because every SceneObject root is a bare `TransformNode` by
    // construction (never a Mesh), so there is no geometry to miss there.
    const visit = (node: Node): void => {
        if (resolve) {
            const owner = resolve(node);
            if (owner && owner !== object) return; // nested SceneObject — its own record
        }
        if (isEditorHelperMesh(node)) {
            excluded?.push(node);
            return;
        }
        owned.push(node);
        for (const child of node.getChildren()) visit(child);
    };
    for (const child of object.transform.getChildren()) visit(child);
    return owned;
}
